import WebsiteLanguageSelect from '@formosa/shared/components/WebsiteLanguageSelect';
import { captureAuthError, renderAuthError } from '@formosa/shared/i18n/authErrorState';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { REMEMBER_USERNAME_KEY } from '@formosa/shared/constants/auth';
import { useTravelText } from '../app/TravelLocaleContext';
import { useAuth } from './AuthContext';
import { authReturnDestination, guestBrowseDestination } from './authReturn';
import GoogleSignInButton from './GoogleSignInButton';
import PasswordField from './PasswordField';
import PhoneCountryFields from './PhoneCountryFields';
import {
  LOGIN_MODE,
  buildLoginPayload,
  parseRememberKey,
  rememberKeyForLogin,
} from './loginPayload';
import { useRegisterConfig } from './useRegisterConfig';
import './auth.css';

export default function LoginPage() {
  const { login } = useAuth();
  const { locale, t } = useTravelText();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerConfig } = useRegisterConfig();
  const destination = authReturnDestination(location.state?.from);
  const notice = typeof location.state?.notice === 'string' ? location.state.notice : '';

  const availableModes = useMemo(() => {
    const modes = [LOGIN_MODE.ACCOUNT];
    if (registerConfig.emailLogin) modes.push(LOGIN_MODE.EMAIL);
    if (registerConfig.phoneLogin) modes.push(LOGIN_MODE.MOBILE);
    return modes;
  }, [registerConfig.emailLogin, registerConfig.phoneLogin]);

  const [mode, setMode] = useState(LOGIN_MODE.ACCOUNT);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [phoneCountry, setPhoneCountry] = useState({
    countryKey: '',
    countryEnName: '',
    phoneAreaCode: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 只保留安全的公開錯誤 key，於 render 時依目前語系翻譯。
  const [error, setError] = useState(null);
  const errorText = renderAuthError(error, t);

  useEffect(() => {
    if (!availableModes.includes(mode)) setMode(availableModes[0]);
  }, [availableModes, mode]);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_USERNAME_KEY);
    const parsed = parseRememberKey(saved);
    if (!parsed) return;
    setRememberMe(true);
    if (parsed.mode === LOGIN_MODE.MOBILE && availableModes.includes(LOGIN_MODE.MOBILE)) {
      setMode(LOGIN_MODE.MOBILE);
      setMobile(parsed.mobile || '');
      setPhoneCountry((prev) => ({ ...prev, countryEnName: parsed.countryName || '' }));
      return;
    }
    if (parsed.mode === LOGIN_MODE.EMAIL && availableModes.includes(LOGIN_MODE.EMAIL)) {
      setMode(LOGIN_MODE.EMAIL);
      setEmail(parsed.email || '');
      return;
    }
    setMode(LOGIN_MODE.ACCOUNT);
    setUsername(parsed.username || '');
  }, [availableModes]);

  async function submit(event) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const values = {
        username,
        email,
        mobile,
        password,
        countryName: phoneCountry.countryEnName,
      };
      const payload = buildLoginPayload(mode, values);
      await login(payload);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_USERNAME_KEY, rememberKeyForLogin(mode, values));
      } else {
        localStorage.removeItem(REMEMBER_USERNAME_KEY);
      }
      navigate(destination, { replace: true });
    } catch (cause) {
      setError(captureAuthError(cause, `travel.auth.login.failed`));
    } finally {
      setSubmitting(false);
    }
  }

  function browseAsGuest() {
    // 僅還原公開可讀 Return URL；受保護目的地回探索，不擴大 Guest 權限
    navigate(guestBrowseDestination(location.state?.from), { replace: true });
  }

  return (
    <main className="travel-auth">
      <form className="travel-auth__card" onSubmit={submit}>
        <Link className="travel-auth__brand" to="/explore">Formosa Travel</Link>
        <WebsiteLanguageSelect locale={locale} t={t} />
        <h1>{t(`travel.auth.login.title`)}</h1>
        <p>{t(`travel.auth.login.subtitle`)}</p>

        {notice ? <div className="travel-auth__success" role="status">{notice}</div> : null}
        {errorText ? <div className="travel-auth__error" role="alert">{errorText}</div> : null}

        <div className="travel-auth__tabs" role="tablist" aria-label={t(`travel.auth.login.methodLabel`)}>
          <button type="button" role="tab" aria-selected={mode === LOGIN_MODE.ACCOUNT}
            className={mode === LOGIN_MODE.ACCOUNT ? 'is-active' : ''}
            onClick={() => setMode(LOGIN_MODE.ACCOUNT)}>{t(`travel.auth.login.account`)}</button>
          {availableModes.includes(LOGIN_MODE.EMAIL) ? (
            <button type="button" role="tab" aria-selected={mode === LOGIN_MODE.EMAIL}
              className={mode === LOGIN_MODE.EMAIL ? 'is-active' : ''}
              onClick={() => setMode(LOGIN_MODE.EMAIL)}>{t(`travel.auth.email`)}</button>
          ) : null}
          {availableModes.includes(LOGIN_MODE.MOBILE) ? (
            <button type="button" role="tab" aria-selected={mode === LOGIN_MODE.MOBILE}
              className={mode === LOGIN_MODE.MOBILE ? 'is-active' : ''}
              onClick={() => setMode(LOGIN_MODE.MOBILE)}>{t(`travel.auth.mobile`)}</button>
          ) : null}
        </div>

        {mode === LOGIN_MODE.ACCOUNT ? (
          <label className="travel-auth__field">
            <span>{t(`travel.auth.login.account`)}</span>
            <input
              required
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              disabled={submitting}
            />
          </label>
        ) : null}

        {mode === LOGIN_MODE.EMAIL ? (
          <label className="travel-auth__field">
            <span>{t(`travel.auth.email`)}</span>
            <input
              required
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={submitting}
            />
          </label>
        ) : null}

        {mode === LOGIN_MODE.MOBILE ? (
          <>
            <PhoneCountryFields
              idPrefix="login-phone"
              countryKey={phoneCountry.countryKey}
              countryEnName={phoneCountry.countryEnName}
              phoneAreaCode={phoneCountry.phoneAreaCode}
              onChange={setPhoneCountry}
              allowedEnNames={registerConfig.enNames}
              disabled={submitting}
            />
            <label className="travel-auth__field">
              <span>{t(`travel.auth.mobile`)}</span>
              <input
                required
                type="tel"
                autoComplete="tel"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                disabled={submitting}
              />
            </label>
          </>
        ) : null}

        <PasswordField
          label={t(`travel.auth.login.password`)}
          required
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={submitting}
        />

        <div className="travel-auth__row">
          <label className="travel-auth__check">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
              disabled={submitting}
            />
            <span>{t(`travel.auth.login.rememberMe`)}</span>
          </label>
          {registerConfig.forgotPassword ? (
            <Link className="travel-auth__muted-link" to="/forgot-password">{t(`travel.auth.login.forgotPassword`)}</Link>
          ) : null}
        </div>

        <button type="submit" className="travel-auth__primary" disabled={submitting}>
          {t(submitting ? `travel.auth.login.submitting` : `travel.auth.login.submit`)}
        </button>

        <button type="button" className="travel-auth__secondary" onClick={browseAsGuest} disabled={submitting}>
          {t(`travel.auth.login.guest`)}
        </button>

        <div className="travel-auth__divider"><span>{t(`travel.auth.or`)}</span></div>
        <GoogleSignInButton onError={(message) => setError(message ? { key: 'authErrors.generic' } : null)} mode="login" />

        <p className="travel-auth__footer">
          <span>{t(`travel.auth.login.noAccount`)}</span>
          {' '}
          <Link to="/register" state={location.state}>{t(`travel.auth.login.registerLink`)}</Link>
        </p>
      </form>
    </main>
  );
}
